import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { XIcon } from 'lucide-react'

import { Input } from '~/design-system/atom/input'

// Memoized Badge component
const Badge = memo(
  ({
    children,
    onRemove,
  }: {
    children: React.ReactNode
    onRemove: () => void
  }) => (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-[12px] font-medium text-blue-700">
      {children}
      <button
        type="button"
        onClick={onRemove}
        className="-mr-1 ml-1 flex items-center justify-center rounded p-0.5 text-blue-600 hover:bg-blue-200 hover:text-blue-800"
        aria-label={`Remove ${children as string}`}
      >
        <XIcon size={12} strokeWidth={2} />
      </button>
    </span>
  ),
)

Badge.displayName = 'Badge'

interface ScopeInputProps {
  value: string // Comma-separated scopes
  onChange: (newValue: string) => void
  label: React.ReactNode
  description?: string
  placeholder?: string
  disabled?: boolean
}

export const ScopeInput: React.FC<ScopeInputProps> = ({
  value,
  onChange,
  label,
  description,
  placeholder = 'Add scope...',
  disabled = false,
}) => {
  const [scopes, setScopes] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const onChangeRef = useRef<typeof onChange>(onChange)

  const updateScopesAndNotify = useCallback(
    (newScopes: string[]) => {
      setScopes(newScopes)
      onChange(newScopes.join(','))
    },
    [onChange],
  )

  useEffect(() => {
    if (!value) {
      setScopes([])
      return
    }
    const scopeArray = Array.isArray(value) ? value : value.split(',')
    const cleanedScopes = scopeArray
      .map((scope) => scope.trim())
      .filter(Boolean)
    onChangeRef.current(cleanedScopes.join(','))
    setScopes(cleanedScopes)
  }, [value, onChangeRef])

  const handleAddScope = useCallback(() => {
    const newScope = inputValue.trim()
    if (newScope && !scopes.includes(newScope) && !disabled) {
      updateScopesAndNotify([...scopes, newScope])
      setInputValue('') // Clear input after adding
    } else {
      setInputValue('') // Clear input even if not added
    }
  }, [inputValue, scopes, disabled, updateScopesAndNotify])

  const handleRemoveScope = useCallback(
    (scopeToRemove: string) => {
      if (disabled) return
      const updatedScopes = scopes.filter((scope) => scope !== scopeToRemove)
      updateScopesAndNotify(updatedScopes)
    },
    [scopes, disabled, updateScopesAndNotify],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault() // Prevent form submission
        handleAddScope()
      }
      // Optional: Handle Backspace to remove the last tag if input is empty
      if (
        event.key === 'Backspace' &&
        inputValue === '' &&
        scopes.length > 0 &&
        !disabled
      ) {
        handleRemoveScope(scopes[scopes.length - 1]!)
      }
    },
    [inputValue, scopes, disabled, handleAddScope, handleRemoveScope],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
    },
    [],
  )

  return (
    <div className="space-y-2">
      {label && (
        <label className="font-cera text-[15px] font-[500] capitalize leading-none text-black-400">
          {label}
        </label>
      )}
      {description && (
        <p className="mt-1 font-gilroy text-[13px] font-[400] text-black-800">
          {description}
        </p>
      )}
      <div
        className={`mt-2 flex flex-wrap items-center gap-2 rounded-md border border-gray-300 p-2 ${disabled ? 'cursor-not-allowed bg-gray-100' : 'bg-white'}`}
      >
        {scopes.map((scope) => (
          <Badge key={scope} onRemove={() => handleRemoveScope(scope)}>
            {scope}
          </Badge>
        ))}
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`flex-grow rounded border-none bg-transparent p-0.5 px-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-blue-300 ${disabled ? 'cursor-not-allowed placeholder:text-gray-400' : 'placeholder:text-gray-500'}`}
          disabled={disabled}
          size={32}
        />
      </div>
    </div>
  )
}
